import React, { useRef, useState } from 'react';
import { StyledGroupedFarmCart } from './style';
import { CartCard } from 'ui/Card/CartCard';
import { useIntl } from 'react-intl';
import { Button, Icon, Input, Modal } from 'ui';
import { notification, message } from 'antd';
import {
  CartActionType,
  FarmProduct,
  useCartDispatch,
  useCartState,
} from 'public/context/CartContext';
import DeliveryForm from '../DeliveryForm/DeliveryForm';
import { Form } from 'antd';
import useQueryApiClient from 'utils/useQueryApiClient';
import { StyledDeliveryForm } from '../DeliveryForm/style';
import { StyledDeliveryDrawerFooter } from 'ui/Drawer/Drawers/style';
import { StyledCartDeliveryModal } from 'ui/Modal/style';
import DeliveryDrawer from 'ui/Drawer/Drawers/DeliveryDrawer';
import { useSearchParams } from 'react-router-dom';
import { useCoupons } from '../CartSummary/useCoupons';
import useMarketType from 'utils/useMarketType';
import CartSelect from '../../Selects/CartSelect';
import { useBreakpoint } from 'utils/useBreakpoint';

interface GroupedFarmCartProps {
  farmProducts: FarmProduct[];
  isView?: boolean | undefined;
}

const GroupedFarmCart = ({ farmProducts, isView }: GroupedFarmCartProps) => {
  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState<boolean>(false);
  const [selectedCart, setSelectedCart] = useState<string>();
  const [cartSavedName, setSavedCartName] = useState<string>();
  const [reload, setReload] = useState<number>(0);

  const { coupons } = useCoupons();
  const intl = useIntl();
  const dispatchCart = useCartDispatch();
  const { priceTotal, cartId, cartName } = useCartState();
  const [notificationApi, contextHolder] = notification.useNotification();
  const [searchParams] = useSearchParams();
  const { isLgDown, isSmUp, isLgUp } = useBreakpoint();

  const { marketType, isEmarket } = useMarketType();

  const status = searchParams.get('status');

  const [deliveryForm] = Form.useForm();

  const deliverySubmitCallbacks = useRef<(() => void)[]>([]);

  useQueryApiClient({
    request: {
      url: `api/v2/products/cart/refetch`,
      method: 'POST',
      data: {
        farmProducts,
        priceTotal: Math.max(priceTotal, 0),
        coupons,
        marketType: marketType,
        cartId: cartId,
      },
      enableOnMount: !(isView || status),
    },
    onSuccess: (response) => {
      if (response.cartUpdated) openNotificationCartUpdated();
      dispatchCart({
        type: CartActionType.REFETCH_CART_PRODUCTS,
        farmProducts: response.farmProducts,
        priceTotal: response.priceTotal,
        requiresAuth: response.requires_auth,
        cartId: response.cartId,
        cartName: response.cartName,
        marketType: response.marketType,
      });
    },
  });

  const { appendData } = useQueryApiClient({
    request: {
      url: `api/v2/cart/:id`,
      disableOnMount: true,
    },
    onSuccess: (response) => {
      if (response.cartUpdated) openNotificationCartUpdated();
      dispatchCart({
        type: CartActionType.REFETCH_CART_PRODUCTS,
        farmProducts: response.farmProducts,
        priceTotal: response.priceTotal,
        requiresAuth: response.requires_auth,
        cartId: response.cartId,
        cartName: response.cartName,
        marketType: response.marketType,
      });
    },
  });

  const { appendData: cartSave } = useQueryApiClient({
    request: {
      url: `api/v2/cart/:id`,
      method: 'PATCH',
    },
    onSuccess: () => setReload((old) => old + 1),
  });

  const onFinishDeliveryDrawer = async () => {
    deliverySubmitCallbacks.current.forEach((callback) => callback());
    setIsCartDrawerOpen(false);
  };

  const registerSubmitCallback = (submitCallback: () => void) => {
    deliverySubmitCallbacks.current.push(submitCallback);
  };

  const openNotificationCartUpdated = () => {
    notificationApi['warning']({
      message: intl.formatMessage({ id: 'cart.updated_message' }),
      description: intl.formatMessage({ id: 'cart.updated_description' }),
      showProgress: true,
      pauseOnHover: true,
      duration: 10,
    });
  };

  const handleAddDelivery = () => {
    deliveryForm
      .validateFields()
      .then(() => {
        deliveryForm.submit();
      })
      .catch(() => {
        message.error(intl.formatMessage({ id: 'errors.missingDeliveries' }));
      });
  };

  const submitDeliveryFooter: JSX.Element = (
    <StyledDeliveryDrawerFooter>
      <Button
        className={
          isLgDown ? 'delivery-drawer-btn mobile' : 'delivery-drawer-btn'
        }
        label={intl.formatMessage({ id: 'general.add_delivery' })}
        type="primary"
        onClick={handleAddDelivery}
        icon={isLgDown ? <Icon name={'arrow-right-red'} /> : null}
      />
    </StyledDeliveryDrawerFooter>
  );

  return (
    <StyledGroupedFarmCart>
      {marketType === 'WHOLESALE' && !status && (
        <CartSelect
          name="cart_id"
          label={intl.formatMessage({ id: 'cart.my_carts' })}
          onChange={(value) => {
            if (value === 'NEW') {
              dispatchCart({
                type: CartActionType.CLEAR_CART,
              });
              return;
            }

            if (cartName) {
              appendData([], { id: value });
            } else {
              setSelectedCart(value);
            }
          }}
          layout="vertical"
          initialValue={cartName ? cartId : undefined}
          reload={reload}
          showNew
        />
      )}
      {contextHolder}
      {isView && (
        <div className={'order-title'}>
          {intl.formatMessage({ id: 'cart.your_order_title' }) +
            ' #' +
            searchParams.get('order_id')}
        </div>
      )}
      <div className={`cart-products-container ${isView ? 'view' : ''}`}>
        {farmProducts.map((farmGroup) => (
          <>
            <div className={'cart-products-farm-title'}>
              {farmGroup.farm_name}
            </div>
            <div className="cart-products-farm-wrapper" key={farmGroup.farm_id}>
              <div className="cart-products-header">
                <div className="cart-cell-1">
                  {intl.formatMessage({ id: 'cart.products' })}
                </div>
                <div className="cart-cell-2" />
                <div className="cart-cell-3">
                  {intl.formatMessage({ id: 'general.price' })}
                </div>
                <div className="cart-cell-4">
                  {intl.formatMessage({ id: 'cart.amount' })}
                </div>
                <div className="cart-cell-5">
                  {intl.formatMessage({ id: 'cart.totlal' })}
                </div>
              </div>
              <div className="cart-products-content-wrapper">
                {farmGroup.products.map((product) => (
                  <div
                    className="cart-product show-sum-card"
                    key={product.cartProduct?.id}
                  >
                    <h3 className="title">{product.cartProduct?.farm_name}</h3>
                    <CartCard product={product} isView={isView} />
                  </div>
                ))}
                {isEmarket && (
                  <div className="cart-delivery-container">
                    {farmGroup.selectedDelivery ? (
                      <div className="cart-delivery-info">
                        {farmGroup.selectedDelivery.firm_name !== 'self' ? (
                          <>
                            <div>
                              {`${intl.formatMessage({
                                id: `process_purchase.${farmGroup.selectedDelivery.firm_name}`,
                              })}${
                                farmGroup.selectedDelivery.price
                                  ? ` - ${farmGroup.selectedDelivery.price.toFixed(
                                      2
                                    )} €`
                                  : ''
                              }`}
                              {isView ? <span>{' | '}</span> : <br />}
                              {farmGroup.selectedDelivery.name}
                            </div>
                          </>
                        ) : (
                          <>
                            <div>
                              {intl.formatMessage({
                                id: 'process_purchase.buy_locally_placeholder_first_part',
                              })}
                              {isView ? <span> | </span> : <br />}
                              {intl.formatMessage({
                                id: 'process_purchase.buy_locally_placeholder_second_part',
                              })}
                              {' - '}
                              {(farmGroup.selectedDelivery.price ?? 0).toFixed(
                                2
                              )}{' '}
                              €
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="cart-delivery-info" />
                    )}
                    {!isView && (
                      <div
                        className={
                          farmGroup.selectedDelivery
                            ? 'cart-delivery-button choosen'
                            : 'cart-delivery-button'
                        }
                      >
                        <Button
                          icon={
                            farmGroup.selectedDelivery ? (
                              <Icon name={'car-delivery-black'} />
                            ) : (
                              <Icon name={'car-delivery'} />
                            )
                          }
                          type={farmGroup.selectedDelivery ? 'text' : 'primary'}
                          label={
                            farmGroup.selectedDelivery
                              ? isSmUp
                                ? intl.formatMessage({
                                    id: 'cart_summary.change_delivery_method',
                                  })
                                : intl.formatMessage({
                                    id: 'cart_summary.change_delivery',
                                  })
                              : intl.formatMessage({
                                  id: 'general.choose_delivery',
                                })
                          }
                          onClick={() => setIsCartDrawerOpen(true)}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        ))}
      </div>

      {isEmarket && (
        <>
          {isLgUp ? (
            <DeliveryDrawer
              id={'delivery-drawer'}
              isDeliveryDrawerOpen={isCartDrawerOpen}
              onDeliveryClose={() => setIsCartDrawerOpen(false)}
              footer={submitDeliveryFooter}
              closeIcon={
                <Icon name={'delivery-drawer-close'} type={'cartDrawerClose'} />
              }
            >
              <StyledDeliveryForm>
                <Form form={deliveryForm} onFinish={onFinishDeliveryDrawer}>
                  {farmProducts.map((farmProduct, index) => (
                    <DeliveryForm
                      deliveryForm={deliveryForm}
                      farmProduct={farmProduct}
                      key={index}
                      registerSubmitCallback={registerSubmitCallback}
                    />
                  ))}
                </Form>
              </StyledDeliveryForm>
            </DeliveryDrawer>
          ) : (
            <StyledCartDeliveryModal
              open={isCartDrawerOpen}
              closable={true}
              onCancel={() => setIsCartDrawerOpen(false)}
              footer={submitDeliveryFooter}
              wrapClassName={'custom-modal-wrap'}
            >
              <StyledDeliveryForm>
                <Form form={deliveryForm} onFinish={onFinishDeliveryDrawer}>
                  {farmProducts.map((farmProduct, index) => (
                    <DeliveryForm
                      deliveryForm={deliveryForm}
                      farmProduct={farmProduct}
                      key={index}
                      registerSubmitCallback={registerSubmitCallback}
                    />
                  ))}
                </Form>
              </StyledDeliveryForm>
            </StyledCartDeliveryModal>
          )}
        </>
      )}
      <Modal
        open={!!selectedCart}
        onCancel={() => setSelectedCart('')}
        footer={[
          <Button
            type="primary"
            onClick={() => {
              cartSave({ name: cartSavedName }, { id: cartId });

              appendData([], { id: selectedCart });

              setSelectedCart('');
            }}
            label={intl.formatMessage({ id: 'general.yes' })}
          />,
          <Button
            type="primary"
            onClick={() => {
              appendData([], { id: selectedCart });
              setSelectedCart('');
            }}
            label={intl.formatMessage({ id: 'general.no' })}
          />,
        ]}
      >
        {intl.formatMessage({ id: 'cart.want_to_save' })}
        <Input noStyle onChange={(e) => setSavedCartName(e.target.value)} />
      </Modal>
    </StyledGroupedFarmCart>
  );
};

export default GroupedFarmCart;
